// Game audio. Prefers the generated audio files from the asset manifest
// ("audio" map — chant, hit, ko, stings, music loop…); every cue keeps a Web
// Audio synth fallback so a missing/failed file is never fatal. The context is
// created lazily on first user gesture (browser autoplay policy); files are
// fetched eagerly and decoded once the context exists.

/**
 * First/last sample above `threshold`, as seconds — the real content bounds of
 * a decoded loop that carries MP3 encoder delay/padding silence at the ends.
 * Returns {0, 0} for an all-silent buffer (callers skip the trim).
 */
export function findLoopBounds(
  data: Float32Array,
  sampleRate: number,
  threshold = 0.005,
): { start: number; end: number } {
  let first = -1;
  let last = -1;
  for (let i = 0; i < data.length; i++) {
    if (Math.abs(data[i]) > threshold) {
      first = i;
      break;
    }
  }
  if (first === -1) return { start: 0, end: 0 };
  for (let i = data.length - 1; i >= first; i--) {
    if (Math.abs(data[i]) > threshold) {
      last = i;
      break;
    }
  }
  return { start: first / sampleRate, end: (last + 1) / sampleRate };
}

type SfxName =
  | "click"
  | "arm"
  | "hit"
  | "big"
  | "ko"
  | "win"
  | "lose"
  | "draw"
  | "whiff"
  | "fight"
  | "sting_win"
  | "sting_lose"
  | "chant1"
  | "chant2"
  | "chant3"
  | "cutin" // super-move cut-in slam whoosh
  | "tally"; // damage-tally announcer sting

// SfxName → manifest sample key (undefined = synth-only cue).
const SAMPLE: Partial<Record<SfxName, string>> = {
  click: "click",
  hit: "hit",
  big: "hit",
  ko: "ko",
  win: "win",
  lose: "lose",
  draw: "draw",
  whiff: "whiff",
  fight: "fight",
  sting_win: "sting_win",
  sting_lose: "sting_lose",
  chant1: "chant_ro",
  chant2: "chant_sham",
  chant3: "chant_bo",
};

export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private musicSrc: AudioBufferSourceNode | null = null;
  private musicOn = false;
  private fetched = new Map<string, ArrayBuffer>();
  private buffers = new Map<string, AudioBuffer>();
  /** Trimmed loop bounds of the music buffer (encoder-padding fix), cached. */
  private musicBounds: { start: number; end: number } | null = null;
  muted = false;
  /** Fired on every toggleMute — the app shell persists the preference. */
  onMuteChange: ((muted: boolean) => void) | null = null;

  /** Fetches the manifest audio files (fire-and-forget; decode waits for resume). */
  preload(map: Record<string, string> | undefined): void {
    if (!map) return;
    for (const [key, path] of Object.entries(map)) {
      if (typeof path !== "string" || !path) continue;
      void fetch(path)
        .then((r) => (r.ok ? r.arrayBuffer() : null))
        .then((buf) => {
          if (buf) {
            this.fetched.set(key, buf);
            this.decodeAll();
          }
        })
        .catch(() => {
          /* fall back to synth */
        });
    }
  }

  private decodeAll(): void {
    if (!this.ctx) return;
    for (const [key, buf] of Array.from(this.fetched)) {
      this.fetched.delete(key);
      void this.ctx
        .decodeAudioData(buf.slice(0))
        .then((ab) => {
          this.buffers.set(key, ab);
          // If the music loop arrived after startMusic(), swap the synth bed out.
          if (key === "music" && this.musicOn && this.musicTimer !== null) {
            this.stopSynthMusic();
            this.startBufferMusic();
          }
        })
        .catch(() => {
          /* fall back to synth */
        });
    }
  }

  /** Must be called from a user gesture the first time. */
  resume(): void {
    if (!this.ctx) {
      const AC: typeof AudioContext =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      // Honour a persisted mute from a previous visit (set before resume()).
      this.master.gain.value = this.muted ? 0 : 0.9;
      this.master.connect(this.ctx.destination);
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.0;
      this.musicGain.connect(this.master);
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.decodeAll();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(this.muted ? 0 : 0.9, this.ctx.currentTime, 0.02);
    }
    this.onMuteChange?.(this.muted);
    return this.muted;
  }

  /** Plays a decoded sample; returns false when unavailable (synth fallback). */
  private sample(key: string, gain = 1, rate = 1): boolean {
    if (!this.ctx || !this.master || this.muted) return false;
    const buf = this.buffers.get(key);
    if (!buf) return false;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(g);
    g.connect(this.master);
    src.start();
    return true;
  }

  private blip(
    freq: number,
    dur: number,
    type: OscillatorType,
    gain: number,
    slideTo?: number,
  ): void {
    if (!this.ctx || !this.master || this.muted) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noise(dur: number, gain: number, hp: number): void {
    if (!this.ctx || !this.master || this.muted) return;
    const t = this.ctx.currentTime;
    const len = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = hp;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    src.start(t);
  }

  /**
   * Plays the full three-beat chant clip if it's loaded; returns true when it
   * did (callers then skip the per-beat synth cues). Beats stay at 0/0.5/1.0s
   * — the generated clip is cut to that grid.
   */
  playChant(): boolean {
    return this.sample("chant", 0.9);
  }

  play(name: SfxName): void {
    const key = SAMPLE[name];
    if (key && this.sample(key, name === "big" ? 1 : 0.85, name === "big" ? 0.82 : 1)) return;
    switch (name) {
      case "click":
        this.blip(520, 0.07, "square", 0.12);
        break;
      case "arm":
        this.blip(740, 0.09, "triangle", 0.16, 980);
        break;
      case "hit":
        this.noise(0.12, 0.25, 900);
        this.blip(180, 0.12, "sawtooth", 0.18, 90);
        break;
      case "big":
        this.noise(0.28, 0.4, 500);
        this.blip(140, 0.35, "sawtooth", 0.3, 60);
        break;
      case "ko":
        this.noise(0.5, 0.5, 300);
        this.blip(110, 0.6, "sawtooth", 0.35, 40);
        break;
      case "win":
      case "sting_win":
        this.blip(523, 0.14, "triangle", 0.22);
        setTimeout(() => this.blip(659, 0.14, "triangle", 0.22), 120);
        setTimeout(() => this.blip(784, 0.24, "triangle", 0.24), 240);
        break;
      case "lose":
      case "sting_lose":
        this.blip(392, 0.18, "sawtooth", 0.18, 300);
        setTimeout(() => this.blip(262, 0.35, "sawtooth", 0.18, 180), 160);
        break;
      case "draw":
        this.blip(440, 0.1, "square", 0.14);
        break;
      case "whiff":
        this.noise(0.08, 0.1, 1600);
        this.blip(300, 0.1, "triangle", 0.1, 180);
        break;
      case "fight":
        this.blip(660, 0.12, "square", 0.2, 880);
        break;
      case "chant1":
        this.blip(440, 0.18, "square", 0.2);
        break;
      case "chant2":
        this.blip(554, 0.18, "square", 0.2);
        break;
      case "chant3":
        this.blip(880, 0.32, "square", 0.26, 1040);
        this.noise(0.1, 0.15, 1200);
        break;
      case "cutin":
        // Rising whoosh + slam for the cinematic cut-in.
        this.noise(0.22, 0.22, 600);
        this.blip(180, 0.24, "sawtooth", 0.2, 720);
        break;
      case "tally":
        // Two-note announcer sting under the damage tally.
        this.blip(587, 0.1, "square", 0.2);
        setTimeout(() => this.blip(880, 0.22, "square", 0.24), 100);
        break;
    }
  }

  startMusic(): void {
    if (!this.ctx || !this.musicGain || this.musicOn) return;
    this.musicOn = true;
    this.musicGain.gain.setTargetAtTime(0.12, this.ctx.currentTime, 0.5);
    if (this.buffers.has("music")) this.startBufferMusic();
    else this.startSynthMusic();
  }

  private startBufferMusic(): void {
    if (!this.ctx || !this.musicGain) return;
    const buf = this.buffers.get("music");
    if (!buf) return;
    this.musicSrc = this.ctx.createBufferSource();
    this.musicSrc.buffer = buf;
    this.musicSrc.loop = true;
    // MP3 encoder delay/padding leaves silence at both ends of the decoded
    // buffer; looping the raw buffer produces an audible dropout every pass.
    // Loop only the actual content (scanned once and cached).
    if (!this.musicBounds) {
      this.musicBounds = findLoopBounds(buf.getChannelData(0), buf.sampleRate);
    }
    const { start, end } = this.musicBounds;
    if (end > start) {
      this.musicSrc.loopStart = start;
      this.musicSrc.loopEnd = end;
    }
    this.musicSrc.connect(this.musicGain);
    this.musicSrc.start(0, end > start ? start : 0);
  }

  private startSynthMusic(): void {
    if (this.musicTimer !== null) return;
    // A jaunty minor arcade riff, one note every 150ms.
    const scale = [220, 261.6, 293.66, 329.63, 392, 329.63, 293.66, 261.6];
    this.musicTimer = window.setInterval(() => {
      if (!this.ctx || !this.musicGain || this.muted) return;
      const t = this.ctx.currentTime;
      const f = scale[this.musicStep % scale.length];
      this.musicStep++;
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f * (this.musicStep % 16 < 8 ? 1 : 1.5);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.5, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
      osc.connect(g);
      g.connect(this.musicGain);
      osc.start(t);
      osc.stop(t + 0.16);
      if (this.musicStep % 4 === 0) this.noise(0.05, 0.05, 2000);
    }, 150);
  }

  private stopSynthMusic(): void {
    if (this.musicTimer !== null) {
      clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
  }

  stopMusic(): void {
    this.musicOn = false;
    this.stopSynthMusic();
    if (this.musicSrc) {
      try {
        this.musicSrc.stop();
      } catch {
        /* already stopped */
      }
      this.musicSrc = null;
    }
    if (this.ctx && this.musicGain) {
      // 0.5s fade — matches the source's select→battle music fade choreography.
      this.musicGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.3);
    }
  }
}
